import { EventEmitter } from '@stencil/core';

export const deferEvent = (event: EventEmitter): EventEmitter => {
  return debounceEvent(event, 0);
};

export const debounceEvent = (
  event: EventEmitter,
  wait: number
): EventEmitter => {
  const original = (event as any)._original || event;
  return {
    _original: event,
    emit: debounce(original.emit.bind(original), wait),
  } as EventEmitter;
};

/**
 * debounce ensures that one function call is made for an event that fires multiple times.
 * @param callback to perform after waiting
 * @param wait how to long to wait before calling the function
 * @returns a debounced function to call as often as required
 */
export const debounce = (callback: (...args: any[]) => void, wait = 0) => {
  let timer: any;
  return (...args: any[]): any => {
    clearTimeout(timer);
    timer = setTimeout(callback, wait, ...args);
  };
};

/**
 * throttle delays executing a function - reducing the function calls of an event that fires multiple times.
 * @param callback to perform after a delay
 * @param delay how long to delay before calling the function
 * @returns a throttled function to call as often as required
 */
export function throttle(callback: (...args: [any]) => void, delay: number) {
  let isThrottled = false;
  let context: any;

  function wrapper(...args) {
    if (isThrottled) {
      context = this;
      return;
    }

    isThrottled = true;
    callback.apply(this, args);

    setTimeout(() => {
      isThrottled = false;
      if (args) {
        wrapper.apply(context, args);
        args = context = null;
      }
    }, delay);
  }

  return wrapper;
}
